import { useMemo } from "react";
import { DataTable } from "@/components/ui/DataTable";
import type { DataTableColumn } from "@/components/ui/DataTable";
import { formatNumberToDisplay, safeParseDecimal } from "@/lib/api/common";
import Decimal from "decimal.js";
import type { TaggedRiskRow, TaggedProfile } from "@/lib/api/riskV3";
import { aggregateV3ProfileItems } from "./V3ProfileCards";
import type { V3CustomerCommodityProfileItem } from "./V3ProfileCards";

// ============================================================================
// Props
// ============================================================================

interface V3TopLevelSummaryTableProps {
  rows: TaggedRiskRow[];
  profiles: TaggedProfile[];
  customerNameMap: Record<string, string>;
  cropYear: number;
}

// ============================================================================
// Component
// ============================================================================

export function V3TopLevelSummaryTable({ rows, profiles, customerNameMap, cropYear }: V3TopLevelSummaryTableProps) {
  const items = useMemo(
    () => aggregateV3ProfileItems(rows, profiles, customerNameMap),
    [rows, profiles, customerNameMap],
  );

  const columns = useMemo(() => {
    const percentSortingFn = (a: any, b: any, columnId: string) => {
      return safeParseDecimal({})(a.getValue(columnId)?.percent || 0).sub(b.getValue(columnId)?.percent || 0).toNumber();
    };

    const computeAggregationFn = (key: string) => (_column: any, rows: any) => {
      const base = rows.reduce((acc: any, row: any) => {
        acc.production = acc.production.plus(row.original.production || 0);
        acc.quantity = acc.quantity.plus(row.original[key] || 0);
        return acc;
      }, {
        quantity: new Decimal(0),
        production: new Decimal(0),
      });

      if (base.production.isZero()) {
        return { value: base.quantity.toNumber(), percent: 0 };
      }

      return {
        value: base.quantity.toNumber(),
        percent: safeParseDecimal({})(base.quantity).div(base.production).times(100),
      };
    };

    const computeAggregatedWithFindValue = (findValue: (row: V3CustomerCommodityProfileItem) => number) => (_column: any, rows: any) => {
      const base = rows.reduce((acc: any, row: any) => {
        acc.production = acc.production.plus(row.original.production || 0);
        acc.quantity = acc.quantity.plus(findValue(row.original) || 0);
        return acc;
      }, { quantity: new Decimal(0), production: new Decimal(0) });

      if (base.production.isZero()) {
        return { value: base.quantity.toNumber(), percent: 0 };
      }

      return {
        value: base.quantity.toNumber(),
        percent: safeParseDecimal({})(base.quantity).div(base.production).times(100),
      };
    };

    const computeAggreagteAverageNonFalsy = (column: any, rows: any) => {
      const column_key = typeof column === "string" ? column : column.id;

      const base = rows.reduce((acc: any, row: any) => {
        if (!row.original[column_key]) return acc;
        acc.count = acc.count + 1;
        acc.total = acc.total.plus(row.original[column_key] || 0);
        return acc;
      }, { count: 0, total: new Decimal(0) });

      if (base.count === 0) return 0;
      return base.total.div(base.count).toNumber();
    };

    // Helper to compute percent_physical from row
    const getPercentPhysical = (row: V3CustomerCommodityProfileItem) => {
      const production = row.production;
      if (production === 0) return 0;
      const physicalTotal = row.basis_quantity + row.otc_basis_quantity + row.cash_physical_quantity + row.dpq_basis_quantity + row.direct_sale_physical_quantity + row.dpq_unset_quantity;
      return (physicalTotal / production) * 100;
    };

    // Helper to compute percent_hedged_w_offer (hedged + open offers) from row
    const getPercentHedgedWOffer = (row: V3CustomerCommodityProfileItem) => {
      const production = row.production;
      if (production === 0) return 0;
      const futuresTotal = row.hta_quantity + row.otc_futures_quantity + row.brokerage_quantity + row.cash_quantity + row.dpq_futures_quantity + row.direct_sale_futures_quantity;
      const totalWithOffer = futuresTotal + row.open_offer_quantity;
      return (totalWithOffer / production) * 100;
    };

    // Helper to get the quantity backing percent_hedged for aggregation
    const getPercentHedgedQuantity = (row: V3CustomerCommodityProfileItem) => {
      return row.hta_quantity + row.otc_futures_quantity + row.brokerage_quantity + row.cash_quantity + row.dpq_futures_quantity + row.direct_sale_futures_quantity;
    };

    // Helper to get the quantity backing percent_physical for aggregation
    const getPercentPhysicalQuantity = (row: V3CustomerCommodityProfileItem) => {
      return row.basis_quantity + row.otc_basis_quantity + row.cash_physical_quantity + row.dpq_basis_quantity + row.direct_sale_physical_quantity + row.dpq_unset_quantity;
    };

    const cols: DataTableColumn<V3CustomerCommodityProfileItem>[] = [
      {
        id: "crop_year",
        header: "Crop Year",
        accessorFn: () => cropYear,
        cell: ({ getValue }: any) => getValue(),
      },
      {
        id: "commodity_display_name",
        accessorKey: "commodity_display_name",
        header: "Commodity",
        cell: ({ getValue }: any) => getValue(),
      },
      {
        id: "customer_id",
        accessorKey: "customer_id",
        header: "Customer",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.customer_name,
        cell: ({ getValue }: any) => getValue(),
      },
      {
        id: "acreage",
        accessorKey: "acreage",
        header: "Acreage",
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as number)}
          </span>
        ),
        aggregatedCell: ({ getValue }: any) => formatNumberToDisplay(getValue()),
      },
      {
        id: "production",
        header: "Production",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.production,
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as number)}
          </span>
        ),
        aggregatedCell: ({ getValue }: any) => formatNumberToDisplay(getValue()),
      },
      {
        id: "projected_production",
        accessorKey: "projected_production",
        header: "Projected Production",
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as number)}
          </span>
        ),
        aggregatedCell: ({ getValue }: any) => formatNumberToDisplay(getValue()),
      },
      {
        id: "actual_production",
        accessorKey: "actual_production",
        header: "Actual Production",
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as number)}
          </span>
        ),
        aggregatedCell: ({ getValue }: any) => formatNumberToDisplay(getValue()),
      },
      {
        id: "projected_yield",
        accessorKey: "projected_yield",
        header: "Projected Yield",
        filterFn: "numberFilter" as any,
        aggregationFn: "mean" as any,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as number)}
          </span>
        ),
        aggregatedCell: ({ getValue }: any) => formatNumberToDisplay(getValue()),
      },
      {
        id: "actual_yield",
        accessorKey: "actual_yield",
        header: "Actual Yield",
        filterFn: "numberFilter" as any,
        aggregationFn: computeAggreagteAverageNonFalsy,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as number)}
          </span>
        ),
        aggregatedCell: ({ getValue }: any) => formatNumberToDisplay(getValue()),
      },
      {
        id: "total_yield",
        accessorKey: "total_yield",
        header: "Yield",
        filterFn: "numberFilter" as any,
        aggregationFn: "mean" as any,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as number)}
          </span>
        ),
        aggregatedCell: ({ getValue }: any) => formatNumberToDisplay(getValue()),
      },
      {
        id: "percent_hedged",
        accessorKey: "percent_hedged",
        header: "Hedged %",
        filterFn: "percentageFilter" as any,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {(getValue() as number).toFixed(2)}%
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue(getPercentHedgedQuantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
      },
      {
        id: "percent_hedged_w_offer",
        header: "Hedged W Offer",
        filterFn: "percentageFilter" as any,
        accessorFn: (row: V3CustomerCommodityProfileItem) => getPercentHedgedWOffer(row),
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string)}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row: V3CustomerCommodityProfileItem) => getPercentHedgedQuantity(row) + row.open_offer_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
      },
      {
        id: "percent_physical",
        header: "Physical %",
        filterFn: "percentageFilter" as any,
        accessorFn: (row: V3CustomerCommodityProfileItem) => getPercentPhysical(row),
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {(getValue() as number).toFixed(2)}%
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue(getPercentPhysicalQuantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
      },
      // ---- Futures Breakdown ----
      {
        id: "futures_hta_qty",
        header: "HTA Qty",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.hta_quantity,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string)}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.hta_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "futures_hta_pct",
        header: "HTA %",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.futuresBreakdown.find((item) => item.id === "hta")?.percent || 0,
        sortingFn: percentSortingFn,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string, { suffix: "%" })}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.hta_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "futures_otc_qty",
        header: "OTC (Futures) Qty",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.otc_futures_quantity,
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string)}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.otc_futures_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "futures_otc_pct",
        header: "OTC (Futures) %",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.futuresBreakdown.find((item) => item.id === "otc")?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string, { suffix: "%" })}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.otc_futures_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "futures_brokerage_qty",
        header: "Brokerage Qty",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.brokerage_quantity,
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string)}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.brokerage_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "futures_brokerage_pct",
        header: "Brokerage %",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.futuresBreakdown.find((item) => item.id === "brokerage")?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string, { suffix: "%" })}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.brokerage_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "futures_dpq_qty",
        header: "DPQ (Futures) Qty",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.dpq_futures_quantity,
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string)}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.dpq_futures_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "futures_dpq_pct",
        header: "DPQ (Futures) %",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.futuresBreakdown.find((item) => item.id === "dpq_futures")?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string, { suffix: "%" })}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.dpq_futures_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "futures_open_offers_qty",
        header: "Open Offers Qty",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.open_offer_quantity,
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string)}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.open_offer_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "futures_open_offers_pct",
        header: "Open Offers %",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.futuresBreakdown.find((item) => item.id === "open_offer_id")?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string, { suffix: "%" })}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.open_offer_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "futures_direct_sales_qty",
        header: "Direct Sales (Futures) Qty",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.direct_sale_futures_quantity,
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string)}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.direct_sale_futures_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "futures_direct_sales_pct",
        header: "Direct Sales (Futures) %",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.futuresBreakdown.find((item) => item.id === "direct_futures")?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string, { suffix: "%" })}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.direct_sale_futures_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "futures_unprotected_qty",
        header: "Unprotected Qty",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.unprotected_futures,
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as number)}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.unprotected_futures),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "futures_unprotected_pct",
        header: "Unprotected %",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.futuresBreakdown.find((item) => item.id === "unprotected_future")?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as number, { suffix: "%" })}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.unprotected_futures),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      // ---- Physical Breakdown ----
      {
        id: "physical_basis_qty",
        header: "Basis Qty",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.basis_quantity,
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string)}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.basis_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "physical_basis_pct",
        header: "Basis %",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.physicalBreakdown.find((item) => item.id === "basis_physical")?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string, { suffix: "%" })}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.basis_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "physical_otc_qty",
        header: "OTC (Basis) Qty",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.otc_basis_quantity,
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string)}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.otc_basis_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "physical_otc_pct",
        header: "OTC (Basis) %",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.physicalBreakdown.find((item) => item.id === "otc_physical")?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string, { suffix: "%" })}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.otc_basis_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "physical_dpq_qty",
        header: "DPQ (Basis) Qty",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.dpq_basis_quantity,
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string)}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.dpq_basis_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "physical_dpq_pct",
        header: "DPQ (Basis) %",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.physicalBreakdown.find((item) => item.id === "dpq_physical")?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string, { suffix: "%" })}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.dpq_basis_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "oddities_physical_qty",
        header: "Direct Sales (Basis) Qty",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.direct_sale_physical_quantity,
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string)}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.direct_sale_physical_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "oddities_physical_pct",
        header: "Direct Sales (Basis) %",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.physicalBreakdown.find((item) => item.id === "direct_physical")?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string, { suffix: "%" })}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.direct_sale_physical_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "physical_cash_qty",
        header: "Cash Qty",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.cash_physical_quantity,
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string)}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.cash_physical_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "physical_cash_pct",
        header: "Cash %",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.physicalBreakdown.find((item) => item.id === "cash_physical")?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as string, { suffix: "%" })}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.cash_physical_quantity),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "physical_unprotected_qty",
        header: "Unsold Qty",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.unprotected_physical,
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as number)}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.unprotected_physical),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "physical_unprotected_pct",
        header: "Unsold %",
        accessorFn: (row: V3CustomerCommodityProfileItem) => row.physicalBreakdown.find((item) => item.id === "unprotected_physical")?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm">
            {formatNumberToDisplay(getValue() as number, { suffix: "%" })}
          </span>
        ),
        aggregationFn: computeAggregatedWithFindValue((row) => row.unprotected_physical),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
    ];

    return cols;
  }, [cropYear]);

  // Filter out items with zero acreage (matching original behavior)
  const filteredItems = useMemo(() => {
    return items.filter((item) => item.acreage !== 0);
  }, [items]);

  return (
    <div className="gap-4 mt-4">
      <h3 className="text-lg font-bold">Risk Overview</h3>
      <DataTable
        tableId="V3TopLevelSummaryTable"
        persistColumnSettings
        className="mx-4"
        tableClassName="max-h-[800px] overflow-y-auto"
        columns={columns}
        data={filteredItems}
        stickyHeaders
        enableColumnVisibility
        enableSorting
        enableMultiSort
        enableFiltering
        enableGrouping
        enableColumnResizing
        enableExport
        enableVirtualization
        enableColumnPinning
        useSteppedGroupingExpansion
        virtualizationRowHeight={49}
        virtualizationOverscan={1}
        filterStyle="select"
        filterMode="internal"
        emptyMessage={filteredItems.length === 0 ? "No data available" : "No data available"}
        groupBy={["commodity_display_name"]}
        defaultColumnVisibility={{ actual_production: false, projected_production: false, crop_year: false }}
        defaultSorting={[
          { id: "commodity_display_name", desc: false },
          { id: "customer_id", desc: false },
        ]}
      />
    </div>
  );
}
